import React, { useState } from "react";
import Profile from "./components/Profile";
import Information from "./components/Information";
import { useAgentInfo } from "layouts/agent/agent-info/_context/AgentInfoContext";
import toast from "react-hot-toast";
import { handleAxiosAndCaughtError } from "utils";
import { useTranslation } from "react-i18next";

const MainPage = () => {
  const { t } = useTranslation();
  const { agentInfo, editAgent } = useAgentInfo();
  const [isLoading, setIsLoading] = useState(false);
  const [isRegenerateSuggestions, setIsRegenerateSuggestions] = useState(true);
  const [formData, setFormData] = useState({
    name: agentInfo.name,
    description: agentInfo.description,
    icon: null as File | null,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;

    if (name === "icon" && files && files.length > 0) {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Check if name i  s empty
    if (!formData.name) {
      return;
    }

    setIsLoading(true);
    try {
      // Check if description has changed
      const descriptionChanged = formData.description !== agentInfo.description;
      const updateData: any = {
        ...(formData.name.length > 0 && { name: formData.name }),
        ...(formData.description.length > 0 && {
          description: formData.description,
        }),
      };

      // Only include is_regenerate_suggestions if description changed
      if (descriptionChanged) {
        updateData.is_regenerate_suggestions = isRegenerateSuggestions;
      }

      await editAgent(updateData);
      toast.success(t("agents.mainPage.agentUpdated"));
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const isButtonDisabled =
    isLoading ||
    (formData.name === agentInfo.name &&
      formData.description === agentInfo.description) ||
    formData.name.length === 0;

  return (
    <div className="relative">
      <form onSubmit={handleSubmit} className="relative  ">
        <fieldset disabled={isLoading}>
          <div
            className={`flex flex-col gap-3 ${isLoading ? "opacity-50" : ""}`}
          >
            <Profile
              agentName={agentInfo.name}
              agentIcon={
                formData.icon
                  ? URL.createObjectURL(formData.icon)
                  : agentInfo.icon_uri
              }
              agentId={agentInfo.id}
              formData={formData}
              onChange={handleInputChange}
            />
            <Information
              value={formData.description}
              onChange={handleTextAreaChange}
              originalDescription={agentInfo.description}
              isRegenerateSuggestions={isRegenerateSuggestions}
              onRegenerateChange={setIsRegenerateSuggestions}
            />
          </div>
          <button
            type="submit"
            disabled={isButtonDisabled}
            className={`linear ml-auto mt-4 block w-44 cursor-pointer rounded-xl bg-brand-900 px-6 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90 ${
              isButtonDisabled ? "cursor-not-allowed opacity-50" : ""
            }`}
          >
            {isLoading
              ? t("agents.mainPage.saving")
              : t("agents.mainPage.saveChanges")}
          </button>
        </fieldset>
      </form>
    </div>
  );
};

export default MainPage;
